import { api, get } from './client';
import type { ApiSuccess } from '@/types/api';
import type {
  Category,
  Collection,
  DeliveryAvailability,
  DeliveryZone,
  HomePayload,
  ProductCard,
  ProductDetail,
  ProductListMeta,
  LocalSeoPage,
  ProductListResult,
  Review,
  ReviewSummary,
} from '@/types/storefront';

/** Ürün listeleme sorgusu — URL parametreleriyle birebir aynı adlar. */
export interface ProductQuery {
  category?: string;
  collection?: string;
  q?: string;
  priceMin?: string;
  priceMax?: string;
  color?: string[];
  flower?: string[];
  sameDay?: boolean;
  bestseller?: boolean;
  isNew?: boolean;
  discount?: boolean;
  sort?: string;
  page?: number;
  pageSize?: number;
  zone?: string;
}

function toParams(query: ProductQuery): Record<string, string> {
  const params: Record<string, string> = {};

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        params[key] = value.join(',');
      }
      continue;
    }

    if (typeof value === 'boolean') {
      if (value) {
        params[key] = 'true';
      }
      continue;
    }

    params[key] = String(value);
  }

  return params;
}

export const storefrontApi = {
  /** Ana sayfanın tamamı tek istekte gelir (10 ayrı çağrı yerine). */
  home: (zone?: string) =>
    get<HomePayload>('/home', { params: zone ? { zone } : undefined }),

  categories: () => get<Category[]>('/categories'),
  category: (slug: string) => get<Category>(`/categories/${slug}`),

  collections: () => get<Collection[]>('/collections'),
  collection: (slug: string) => get<Collection>(`/collections/${slug}`),

  async products(query: ProductQuery): Promise<ProductListResult> {
    const response = await api.get<ApiSuccess<ProductCard[]>>('/products', {
      params: toParams(query),
    });

    return {
      items: response.data.data,
      meta: response.data.meta as unknown as ProductListMeta,
    };
  },

  product: (slug: string, zone?: string) =>
    get<ProductDetail>(`/products/${slug}`, {
      params: zone ? { zone } : undefined,
    }),

  similar: (slug: string) => get<ProductCard[]>(`/products/${slug}/similar`),

  search: (term: string) =>
    get<ProductCard[]>('/products/search', { params: { q: term } }),

  reviews: (slug: string) =>
    get<{ items: Review[]; summary: ReviewSummary }>(
      `/products/${slug}/reviews`,
    ),

  zones: () => get<DeliveryZone[]>('/delivery/zones'),

  /** Yerel arama sayfalarının listesi — footer bağlantıları buradan gelir. */
  localPages: () => get<LocalSeoPage[]>('/seo/local'),

  localPage: (slug: string) => get<LocalSeoPage>(`/seo/local/${slug}`),

  availability: (params: { zone?: string; product?: string[]; subtotal?: string }) =>
    get<DeliveryAvailability>('/delivery/availability', {
      params: {
        ...(params.zone ? { zone: params.zone } : {}),
        // Diziler virgülle birleştirilir: axios varsayılanı `product[]=`
        // biçiminde serileştirir ve backend bunu bilinçli olarak reddeder.
        ...(params.product?.length ? { product: params.product.join(',') } : {}),
        ...(params.subtotal ? { subtotal: params.subtotal } : {}),
      },
    }),
};
